import {rootState} from '@mintdata-wsp/common/state/RootState';
import {VisualComponent} from '@mintdata-wsp/common/state/ui/visualDesigner/artifacts/VisualComponent';
import {MeshLocation} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshLocation';
import {MeshState} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshState';
import {AnySheet, AnySlot} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SheetTypes';
import {SlotLocation} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SlotLocation';
import {Workbook} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/Workbook';
import {Box} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/component/Box';
import {Page} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/Page';
import {
    PropertiesVisiblePopover,
    SurfacePoint,
    SurfaceSize
} from '@mintdata-wsp/common/state/ui/visualDesigner/SurfaceEditorState';
import {
    ILearnExperienceActivePageData,
    ILearnExperienceActiveSheetData,
    ILearnExperienceAddComponentData,
    ILearnExperienceCellStateData,
    ILearnExperienceComponentData,
    ILearnExperienceCopyPageFromData,
    ILearnExperienceCurrentContextData,
    ILearnExperienceData,
    ILearnExperienceFrameDockData,
    ILearnExperienceFrameFullOverlayData,
    ILearnExperienceFramePositionRelativeData,
    ILearnExperienceGetBoundsData,
    ILearnExperienceMessage,
    ILearnExperienceMoveComponentData,
    ILearnExperienceSelectedCellData,
    ILearnExperienceSetComponentPositionData,
    ILearnExperienceSetFrameBoundsData,
    ILearnExperienceSetPositionData,
    ILearnExperienceSetSizeData,
    ILearnExperienceUiPartStateData,
    LEARN_EXPERIENCE_MESSAGE,
    LearnExperienceCellState,
    LearnExperienceFramePosition,
    LearnExperienceFunction,
    LearnExperienceRelativeTo,
    LearnExperienceState,
    LearnExperienceUiPart
} from '@mintdata-wsp/common/state/ui/visualDesigner/workbench/LearnExperienceState';
import {IWSPMeshContext} from '@mintdata-wsp/wsp/controllers/visualDesigner/spreadsheet/contexts/MeshContext';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {GUID} from '@mintdata/mint-kit/api/models/GUID';
import {Units} from '@mintdata/mint-kit/api/models/Units';
import {Type} from '@mintdata/mint-kit/api/types/Type';
import {serialize} from '@mintdata/mint-kit/impl/util/serializr/src/serializr';
import bind from 'bind-decorator';
import {action} from 'mobx';

export class LearnExperienceController
{
    private _lxFunctionsSet:{[functionType:string]:(id:GUID, data:ILearnExperienceData) => void;} = {
        [LearnExperienceFunction.SET_POSITION]: this._onSetPosition,
        [LearnExperienceFunction.SET_SIZE]: this._onSetSize,
        [LearnExperienceFunction.GET_FRAME_BOUNDS]: this._onGetFrameBounds,
        [LearnExperienceFunction.SET_FRAME_BOUNDS]: this._onSetFrameBounds,
        [LearnExperienceFunction.COPY_PAGE_FROM]: this._onCopyPageFrom,
        [LearnExperienceFunction.GET_ACTIVE_PAGE]: this._onGetActivePage,
        [LearnExperienceFunction.SET_ACTIVE_PAGE]: this._onSetActivePage,
        [LearnExperienceFunction.GET_ACTIVE_SHEET]: this._onGetActiveSheet,
        [LearnExperienceFunction.SET_ACTIVE_SHEET]: this._onSetActiveSheet,
        [LearnExperienceFunction.GET_SELECTED_COMPONENT]: this._onGetSelectedComponent,
        [LearnExperienceFunction.SET_SELECTED_COMPONENT]: this._onSetSelectedComponent,
        [LearnExperienceFunction.GET_SELECTED_CELL]: this._onGetSelectedCell,
        [LearnExperienceFunction.SET_SELECTED_CELL]: this._onSetSelectedCell,
        [LearnExperienceFunction.CLOSE]: this._onClose,
        [LearnExperienceFunction.GET_CURRENT_CONTEXT]: this._onGetCurrentContext,
        [LearnExperienceFunction.GET_SCREEN_BOUNDS]: this._onGetScreenBounds,
        [LearnExperienceFunction.GET_UI_PART_STATE]: this._onGetUiPartState,
        [LearnExperienceFunction.SET_UI_PART_STATE]: this._onSetUiPartState,
        [LearnExperienceFunction.ADD_COMPONENT]: this._onAddComponent,
        [LearnExperienceFunction.REMOVE_COMPONENT]: this._onRemoveComponent,
        [LearnExperienceFunction.SET_COMPONENT_POSITION]: this._onSetComponentPosition,
        [LearnExperienceFunction.MOVE_COMPONENT]: this._onMoveComponent,
        [LearnExperienceFunction.SET_FRAME_POSITION_RELATIVE]: this._onSetFramePositionRelative,
        [LearnExperienceFunction.LX_GET_CELL_STATE]: this._onGetCellState,
        [LearnExperienceFunction.LX_SET_CELL_STATE]: this._onSetCellState,
        [LearnExperienceFunction.LX_GET_FRAME_DOCK]: this._onGetFrameDock,
        [LearnExperienceFunction.LX_SET_FRAME_DOCK]: this._onSetFrameDock,
        [LearnExperienceFunction.LX_GET_FRAME_FULL_OVERLAY]: this._onGetFrameFullOverlay,
        [LearnExperienceFunction.LX_SET_FRAME_FULL_OVERLAY]: this._onSetFrameFullOverlay
    };

    public getState():LearnExperienceState
    {
        const {ui: {visualDesigner: {lx}}} = rootState;

        return lx;
    }

    public init():void
    {
        window.addEventListener('message', this._onMessage, false);
        window.addEventListener('hashchange', this._onHashChange, false);

        this._onHashChange();
    }

    public postMessage(data:any = {})
    {
        const lxState:LearnExperienceState = this.getState();
        const {iframeEl} = lxState;

        if( !iframeEl )
            return;

        const message:any = {
            type: LEARN_EXPERIENCE_MESSAGE,
            ...data
        };

        iframeEl.contentWindow.postMessage(message, '*');
    }

    @bind
    public onIframeRef(iframeEl:HTMLIFrameElement)
    {
        const lxState:LearnExperienceState = this.getState();

        lxState.iframeEl = iframeEl;
    }

    @action.bound
    private _onHashChange(event?:HashChangeEvent)
    {
        const lxState:LearnExperienceState = this.getState();

        const result:RegExpExecArray = /^(?:.*#)(.*)/.exec(event ? event.newURL : window.location.hash);
        const hash:string = result && result[1] ? result[1] : '';
        const hashParams:URLSearchParams = new URLSearchParams(hash);

        //lxState.isActive = hashParams.get('learn') === '1';
        lxState.isDevMode = hashParams.get('learn-design') != null;
        lxState.isLxDevMode = hashParams.get('learn-dev') === '1';

        if( lxState.isDevMode && hashParams.get('learn-design') )
            lxState.designName = hashParams.get('learn-design');
        else if( !lxState.isDevMode && hashParams.get('learn-app') )
            lxState.designName = hashParams.get('learn-app');
    }

    @bind
    private _onMessage(event:MessageEvent):void
    {
        if( event.source !== window && event.data && event.data.type === LEARN_EXPERIENCE_MESSAGE )
        {
            const lxState:LearnExperienceState = this.getState();

            if( lxState.isActive )
            {
                const message:ILearnExperienceMessage = event.data as ILearnExperienceMessage;

                if( message.data && this._lxFunctionsSet[message.data.lxFunction] )
                    this._lxFunctionsSet[message.data.lxFunction](message.id, message.data);
            }
        }
    }

    private _processLxGetFunc(id:GUID, data:ILearnExperienceData):void
    {
        const lxState:LearnExperienceState = this.getState();
        const {iframeEl} = lxState;

        if( !iframeEl )
            return;

        const message:ILearnExperienceMessage = {
            id,
            type: LEARN_EXPERIENCE_MESSAGE,
            data
        };

        iframeEl.contentWindow.postMessage(message, '*');
    }

    @action.bound
    private _onSetPosition(id:GUID, data:ILearnExperienceSetPositionData):void
    {
        const lxState:LearnExperienceState = this.getState();

        lxState.horizontalAlign = data.horizontalAlign;
        lxState.verticalAlign = data.verticalAlign;
    }

    @action.bound
    private _onSetSize(id:GUID, data:ILearnExperienceSetSizeData):void
    {
        const lxState:LearnExperienceState = this.getState();

        lxState.width = data.width;
        lxState.height = data.height;
    }

    @action.bound
    private _onGetFrameBounds(id:GUID, data:ILearnExperienceData):void
    {
        const lxState:LearnExperienceState = this.getState();

        if( lxState.iframeEl )
        {
            const clientRect:ClientRect = lxState.iframeEl.parentElement.getBoundingClientRect();

            const responseData:ILearnExperienceGetBoundsData = {
                lxFunction: LearnExperienceFunction.GET_FRAME_BOUNDS,
                x: clientRect.left,
                y: clientRect.top,
                width: clientRect.width,
                height: clientRect.height
            };

            this._processLxGetFunc(id, responseData);
        }
    }

    @action.bound
    private _onSetFrameBounds(id:GUID, data:ILearnExperienceSetFrameBoundsData):void
    {
        this._onSetPosition(id, data);
        this._onSetSize(id, data);
    }

    @action.bound
    private async _onCopyPageFrom(id:GUID, data:ILearnExperienceCopyPageFromData):Promise<void>
    {
        const {ui: {copyPages}} = controllers;

        await copyPages.copyPagesFromDesign(
            data.projectId,
            data.designId,
            -1,
            data.pageNames,
            data.importPrefix,
            data.isImportSheets
        );

        const responseData:ILearnExperienceData = {
            lxFunction: LearnExperienceFunction.COPY_PAGE_FROM
        };

        this._processLxGetFunc(id, responseData);
    }

    @action.bound
    private _onGetActivePage(id:GUID, data:ILearnExperienceData):void
    {
        const {ui: {surfaceArea}} = controllers;

        const page:Page = surfaceArea.getActivePage();

        const responseData:ILearnExperienceActivePageData = {
            lxFunction: LearnExperienceFunction.GET_ACTIVE_PAGE,
            pageName: page ? page.name : null
        };

        this._processLxGetFunc(id, responseData);
    }

    @action.bound
    private _onSetActivePage(id:GUID, data:ILearnExperienceActivePageData):void
    {
        const {ui: {surfaceArea}} = controllers;

        const page:Page = surfaceArea.findPageByName(data.pageName);

        if( page )
            surfaceArea.setActivePage(page);
    }

    @action.bound
    private _onGetActiveSheet(id:GUID, data:ILearnExperienceData):void
    {
        const {ui: {spreadsheets: {main, tabs}}} = controllers;

        const rootWorkbook:Workbook = main.getRootWorkbook();
        const sheet:AnySheet = tabs.getActiveSheet(rootWorkbook.id);

        const responseData:ILearnExperienceActiveSheetData = {
            lxFunction: LearnExperienceFunction.GET_ACTIVE_SHEET,
            sheetName: sheet ? sheet.name : null
        };

        this._processLxGetFunc(id, responseData);
    }

    @action.bound
    private _onSetActiveSheet(id:GUID, data:ILearnExperienceActiveSheetData):void
    {
        const {ui: {spreadsheets: {main, workbook, tabs}}} = controllers;

        const rootWorkbook:Workbook = main.getRootWorkbook();
        const sheet:AnySheet = workbook.findSheetByName(rootWorkbook.id, data.sheetName);

        if( sheet )
            tabs.setActiveSheet(rootWorkbook.id, sheet.id);
    }

    @action.bound
    private _onGetSelectedComponent(id:GUID, data:ILearnExperienceData):void
    {
        const {ui: {surfaceArea, surfaceSelection}} = controllers;

        const page:Page = surfaceArea.getActivePage();
        const components:Array<Box> = surfaceSelection.getSelectedBoxes();

        const responseData:ILearnExperienceComponentData = {
            lxFunction: LearnExperienceFunction.GET_ACTIVE_SHEET,
            pageName: page ? page.name : null,
            componentName: components.length === 1 ? components[0].componentDesc.name.toString() : null
        };

        this._processLxGetFunc(id, responseData);
    }

    @action.bound
    private _onSetSelectedComponent(id:GUID, data:ILearnExperienceComponentData):void
    {
        const {ui: {surfaceArea, surfaceSelection}} = controllers;

        const page:Page = surfaceArea.findPageByName(data.pageName);

        if( page )
        {
            const box:Box = surfaceArea.findBoxByName(page.id, data.componentName);

            if( box )
                surfaceSelection.setSingleBoxSelection(box);
        }
    }

    @action.bound
    private _onGetSelectedCell(id:GUID, data:ILearnExperienceData):void
    {
        const {ui: {spreadsheets: {main, workbook, meshSelection, tabs, mesh}}} = controllers;

        const rootWorkbook:Workbook = main.getRootWorkbook();
        const sheet:AnySheet = tabs.getActiveSheet(rootWorkbook.id);

        if( sheet )
        {
            const ctx:IWSPMeshContext = workbook.getMeshContextBySheetId(sheet.id);
            const meshState:MeshState = mesh.getState(ctx);
            const from:MeshLocation = meshState.details.selectionRangeFrom || meshState.details.activeCell;
            const to:MeshLocation = meshState.details.selectionRangeTo;

            const responseData:ILearnExperienceSelectedCellData = {
                lxFunction: LearnExperienceFunction.GET_ACTIVE_SHEET,
                sheetName: sheet ? sheet.name : null,
                cellRange: {
                    from: {
                        columnIndex: from.colIndex,
                        rowIndex: from.rowIndex
                    },
                    to: to
                        ? {
                            columnIndex: to.colIndex,
                            rowIndex: to.rowIndex
                        }
                        : null
                }
            };

            this._processLxGetFunc(id, responseData);
        }
    }

    @action.bound
    private _onSetSelectedCell(id:GUID, data:ILearnExperienceSelectedCellData):void
    {
        const {ui: {spreadsheets: {main, workbook, meshSelection}}} = controllers;

        const rootWorkbook:Workbook = main.getRootWorkbook();
        const sheet:AnySheet = workbook.findSheetByName(rootWorkbook.id, data.sheetName);

        if( sheet && data.cellRange )
        {
            const ctx:IWSPMeshContext = workbook.getMeshContextBySheetId(sheet.id);
            const from:MeshLocation = new MeshLocation(
                data.cellRange.from.columnIndex,
                data.cellRange.from.rowIndex
            );

            if( data.cellRange.to )
            {
                const to:MeshLocation = new MeshLocation(
                    data.cellRange.to.columnIndex,
                    data.cellRange.to.rowIndex
                );

                meshSelection.selectCellRange(ctx, from, to);
            }
            else
                meshSelection.selectCell(ctx, from);
        }
    }

    @action.bound
    private _onClose(id:GUID, data:ILearnExperienceData):void
    {
        const lxState:LearnExperienceState = this.getState();

        lxState.isVisible = false;
    }

    @action.bound
    private _onGetCurrentContext(id:GUID, data:ILearnExperienceData):void
    {
        const {user} = controllers;

        const responseData:ILearnExperienceCurrentContextData = {
            lxFunction: LearnExperienceFunction.GET_ACTIVE_SHEET,
            organizationId: user.getCurrentOrganizationSid(),
            userId: user.getUserId() ? user.getUserId().toString() : null
        };

        this._processLxGetFunc(id, responseData);
    }

    @action.bound
    private _onGetScreenBounds(id:GUID, data:ILearnExperienceData):void
    {
        const responseData:ILearnExperienceGetBoundsData = {
            lxFunction: LearnExperienceFunction.GET_SCREEN_BOUNDS,
            x: 0,
            y: 0,
            width: window.innerWidth,
            height: window.innerHeight
        };

        this._processLxGetFunc(id, responseData);
    }

    @action.bound
    private _onGetUiPartState(id:GUID, data:ILearnExperienceUiPartStateData):void
    {
        const {
            ui: {
                visualDesigner: {
                    workbench: {
                        visibility: {
                            isPagesPanelVisible,
                            isPropertiesPanelVisible,
                            isSpreadsheetVisible,
                            isSpreadsheetFullScreen
                        },
                        cellSettingsBar: {
                            isBarVisible
                        },
                    },
                    surfaceEditor: {
                        propertiesPanel: {
                            propertiesVisiblePopover
                        }
                    },
                    topMenuState: {
                        isExpanded
                    }
                }
            }
        } = rootState;

        let partState:string = 'unknown part';

        switch( data.part )
        {
            case LearnExperienceUiPart.Outline:
                partState = isPagesPanelVisible ? 'open' : 'closed';
                break;
            case LearnExperienceUiPart.Property:
                partState = 'closed';

                if( isPropertiesPanelVisible )
                    partState = propertiesVisiblePopover === PropertiesVisiblePopover.Repeater
                        ? 'open-repeater'
                        : propertiesVisiblePopover === PropertiesVisiblePopover.Loader
                            ? 'open-loader'
                            : propertiesVisiblePopover === PropertiesVisiblePopover.Events
                                ? 'open-events'
                                : 'open';

                break;
            case LearnExperienceUiPart.Spreadsheet:
                partState = isSpreadsheetVisible
                    ? isSpreadsheetFullScreen
                        ? 'open-full-width'
                        : 'open-split-view'
                    : 'closed';
                break;
            case LearnExperienceUiPart.Stylebar:
                partState = isBarVisible ? 'open' : 'closed';
                break;
            case LearnExperienceUiPart.Toolbar:
                partState = isExpanded ? 'full' : 'minimal';
                break;
        }

        const responseData:ILearnExperienceUiPartStateData = {
            lxFunction: LearnExperienceFunction.GET_SCREEN_BOUNDS,
            part: data.part,
            partState,
            partData: null
        };

        this._processLxGetFunc(id, responseData);
    }

    @action.bound
    private _onSetUiPartState(id:GUID, data:ILearnExperienceUiPartStateData):void
    {
        const {ui: {workbenchVisibility, topMenu, spreadsheets: {cellSettingsBar}, surfacePropertiesPanel}} = controllers;

        switch( data.part )
        {
            case LearnExperienceUiPart.Outline:
                workbenchVisibility.setPagesPanelVisibility(data.partState === 'open');
                break;
            case LearnExperienceUiPart.Property:
                workbenchVisibility.setPropertiesPanelVisibility(data.partState !== 'closed');

                surfacePropertiesPanel.setPropertiesPopover(
                    data.partState === 'open-repeater'
                        ? PropertiesVisiblePopover.Repeater
                        : data.partState === 'open-loader'
                        ? PropertiesVisiblePopover.Loader
                        : data.partState === 'open-events'
                            ? PropertiesVisiblePopover.Events
                            : PropertiesVisiblePopover.None
                );

                break;
            case LearnExperienceUiPart.Spreadsheet:
                workbenchVisibility.setSpreadsheetVisibility(data.partState !== 'closed');
                workbenchVisibility.setSpreadsheetFullScreen(data.partState === 'open-full-width');

                const spreadsheetWidth:number = parseFloat(data.partData);

                if( !isNaN(spreadsheetWidth) )
                    workbenchVisibility.setGridRightColumnWidth(spreadsheetWidth);

                break;
            case LearnExperienceUiPart.Stylebar:
                cellSettingsBar.setBarVisibility(data.partState === 'open');
                break;
            case LearnExperienceUiPart.Toolbar:
                topMenu.setTopMenuIsExpanded(data.partState === 'full');
                break;
        }
    }

    @action.bound
    private async _onAddComponent(id:GUID, data:ILearnExperienceAddComponentData):Promise<void>
    {
        const {ui: {surfaceArea, artifacts}} = controllers;

        const page:Page = surfaceArea.findPageByName(data.pageName);

        if( !page )
            return;

        let parentBox:Box = page.artboard.viewport;

        if( data.parentName )
        {
            parentBox = surfaceArea.findBoxByName(page.id, data.parentName);

            if( !parentBox )
                return;
        }

        const component:VisualComponent = Array.from(artifacts.getVisualComponents().values())
            .find((component:VisualComponent) => data.componentType === component.name);

        if( !component )
            return;

        let xOffsetInParent:number = data.x;
        let yOffsetInParent:number = data.y;
        let width:number = data.width;
        let height:number = data.height;

        if( !xOffsetInParent )
            xOffsetInParent = 0;

        if( !yOffsetInParent )
            yOffsetInParent = 0;

        if( !width || !height )
        {
            const initialSize:SurfaceSize = surfaceArea.componentInitialSize(component);

            width = width || initialSize.width;
            height = height || initialSize.height;
        }

        const box:Box = await surfaceArea.addNewBox(
            page,
            parentBox,
            null,
            component,
            data.componentName,
            SurfacePoint.from(xOffsetInParent, yOffsetInParent),
            new SurfaceSize(width, height)
        );

        box.componentDesc.removeLinkNameToProp();

        const responseData:ILearnExperienceData = {
            lxFunction: LearnExperienceFunction.ADD_COMPONENT,
        };

        this._processLxGetFunc(id, responseData);
    }

    @action.bound
    private _onRemoveComponent(id:GUID, data:ILearnExperienceComponentData):void
    {
        const {ui: {surfaceArea}} = controllers;

        const page:Page = surfaceArea.findPageByName(data.pageName);

        if( !page )
            return;

        const box:Box = surfaceArea.findBoxByName(page.id, data.componentName);

        if( !box )
            return;

        surfaceArea.removeVisualComponent(box);
    }

    @action.bound
    private _onSetComponentPosition(id:GUID, data:ILearnExperienceSetComponentPositionData):void
    {
        const {ui: {surfaceArea}} = controllers;

        const page:Page = surfaceArea.findPageByName(data.pageName);

        if( !page )
            return;

        const box:Box = surfaceArea.findBoxByName(page.id, data.componentName);

        if( !box )
            return;

        box.componentDesc.layoutLeftProp.setInnerValue(Units.fromPixels(data.x));
        box.componentDesc.layoutTopProp.setInnerValue(Units.fromPixels(data.y));
    }

    @action.bound
    private _onMoveComponent(id:GUID, data:ILearnExperienceMoveComponentData):void
    {
        const {ui: {surfaceArea}} = controllers;

        const page:Page = surfaceArea.findPageByName(data.pageName);

        if( !page )
            return;

        const box:Box = surfaceArea.findBoxByName(page.id, data.componentName);

        if( !box )
            return;

        box.componentDesc.layoutLeftProp.setInnerValue(
            Units.fromPixels(box.componentDesc.layoutLeft.value.value + data.deltaX)
        );
        box.componentDesc.layoutTopProp.setInnerValue(
            Units.fromPixels(box.componentDesc.layoutTop.value.value + data.deltaY)
        );
    }

    @action.bound
    private _onSetFramePositionRelative(id:GUID, data:ILearnExperienceFramePositionRelativeData):void
    {
        const lxState:LearnExperienceState = this.getState();

        let selector:string = '';

        switch( data.relativeTo )
        {
            case LearnExperienceRelativeTo.ActivationDot:
            case LearnExperienceRelativeTo.SelectedComponent:
            case LearnExperienceRelativeTo.ActiveCell:
            case LearnExperienceRelativeTo.SelectedPage:
            case LearnExperienceRelativeTo.ActiveSheet:
            case LearnExperienceRelativeTo.PropertyPanel:
            case LearnExperienceRelativeTo.RepeaterPanel:
            case LearnExperienceRelativeTo.EventPanel:
                selector = `[data-component="${data.relativeTo}"]`;
                break;
            default:
                selector = data.relativeTo;
                break;
        }

        if( data.relativeTo === LearnExperienceRelativeTo.PropertyPanel
            || data.relativeTo === LearnExperienceRelativeTo.RepeaterPanel
            || data.relativeTo === LearnExperienceRelativeTo.EventPanel )
        {
            const names:Array<string> = data.relativeToName.split('.');

            selector = names.reduce(
                (previousSelector:string, name:string) => previousSelector + ` [data-prop="${name}"]`,
                selector
            );
        }

        const element:HTMLElement = document.querySelector(selector);

        if( element )
        {
            const clientRect:ClientRect = element.getBoundingClientRect();

            switch( data.framePosition )
            {
                case LearnExperienceFramePosition.BelowCentered:
                    lxState.horizontalAlign = `calc(${clientRect.left}px + ${clientRect.width}px / 2 - 50% )`;
                    lxState.verticalAlign = clientRect.bottom;
                    break;
                case LearnExperienceFramePosition.BelowLeftEdgeAligned:
                    lxState.horizontalAlign = clientRect.left;
                    lxState.verticalAlign = clientRect.bottom;
                    break;
                case LearnExperienceFramePosition.BelowRightEdgeAligned:
                    lxState.horizontalAlign = `calc(${clientRect.right}px - 100% )`;
                    lxState.verticalAlign = clientRect.bottom;
                    break;
                case LearnExperienceFramePosition.AboveCentered:
                    lxState.horizontalAlign = `calc(${clientRect.left}px + ${clientRect.width}px / 2 - 50% )`;
                    lxState.verticalAlign = `calc(${clientRect.top}px - 100% )`;
                    break;
                case LearnExperienceFramePosition.AboveLeftEdgeAligned:
                    lxState.horizontalAlign = clientRect.left;
                    lxState.verticalAlign = `calc(${clientRect.top}px - 100% )`;
                    break;
                case LearnExperienceFramePosition.AboveRightEdgeAligned:
                    lxState.horizontalAlign = `calc(${clientRect.right}px - 100% )`;
                    lxState.verticalAlign = `calc(${clientRect.top}px - 100% )`;
                    break;
            }
        }
    }

    @action.bound
    private _onGetCellState(id:GUID, data:ILearnExperienceCellStateData):void
    {
        const {ui: {spreadsheets: {main, workbook, tabs, mesh, sheet: sheetController}}} = controllers;

        const rootWorkbook:Workbook = main.getRootWorkbook();
        const activeSheet:AnySheet = tabs.getActiveSheet(rootWorkbook.id);
        const sheet:AnySheet = workbook.findSheetByName(rootWorkbook.id, data.sheetName);

        if( sheet )
        {
            const ctx:IWSPMeshContext = workbook.getMeshContextBySheetId(sheet.id);
            const meshState:MeshState = mesh.getState(ctx);

            const slotLocation:SlotLocation = new SlotLocation(data.cellRef.columnIndex, data.cellRef.rowIndex);
            const slot:AnySlot = sheetController.findSlot(sheet.id, slotLocation);

            if( slot )
            {
                const isActive:boolean = meshState.details.activeCell.isEqualTo(
                    MeshLocation.fromSlotLocation(slotLocation)
                );
                const isEditing:boolean = main.isEditMode;

                const responseData:ILearnExperienceCellStateData = {
                    lxFunction: LearnExperienceFunction.LX_GET_CELL_STATE,
                    sheetName: sheet ? sheet.name : null,
                    cellRef: data.cellRef,
                    cellState: sheet.id !== activeSheet.id || !isActive
                        ? LearnExperienceCellState.NotActive
                        : isEditing
                            ? LearnExperienceCellState.ActiveEditing
                            : LearnExperienceCellState.Active,
                    isLazy: slot.isLazy,
                    isAccumulating: slot.isAccumulative,
                    isRealtime: slot.isLive,
                    cellFormula: slot.formula && !Type.isInstanceOfIValue(slot.formula)
                        ? slot.formula.toString()
                        : null,
                    cellValue: slot.value ? serialize(slot.value) : null
                };

                this._processLxGetFunc(id, responseData);
            }
        }
    }

    @action.bound
    private _onSetCellState(id:GUID, data:ILearnExperienceCellStateData):void
    {
        const {ui: {spreadsheets: {main, workbook, tabs, mesh, meshSelection, sheet: sheetController}}} = controllers;

        const rootWorkbook:Workbook = main.getRootWorkbook();
        const activeSheet:AnySheet = tabs.getActiveSheet(rootWorkbook.id);
        const sheet:AnySheet = workbook.findSheetByName(rootWorkbook.id, data.sheetName);
        const slotLocation:SlotLocation = new SlotLocation(data.cellRef.columnIndex, data.cellRef.rowIndex);

        if( sheet && sheet.id === activeSheet.id )
        {
            const ctx:IWSPMeshContext = workbook.getMeshContextBySheetId(sheet.id);

            switch( data.cellState )
            {
                case LearnExperienceCellState.Active:
                    meshSelection.selectCell(ctx, MeshLocation.fromSlotLocation(slotLocation));
                    break;
                case LearnExperienceCellState.ActiveEditing:
                    meshSelection.selectCell(ctx, MeshLocation.fromSlotLocation(slotLocation));
                    mesh.startEditingCell(ctx, data.cellFormula);
                    break;
                default:
                    mesh.finishEditingCell(ctx);

                    if( data.cellFormula )
                        sheetController.setSlotFormula(sheet.id, slotLocation, data.cellFormula);
                    break;
            }
        }
    }

    @action.bound
    private _onGetFrameDock(id:GUID, data:ILearnExperienceFrameDockData):void
    {
        const lxState:LearnExperienceState = this.getState();

        const responseData:ILearnExperienceFrameDockData = {
            lxFunction: LearnExperienceFunction.LX_GET_FRAME_DOCK,
            dockSide: lxState.dockSide,
            dockSize: lxState.dockSize
        };

        this._processLxGetFunc(id, responseData);
    }

    @action.bound
    private _onSetFrameDock(id:GUID, data:ILearnExperienceFrameDockData):void
    {
        const lxState:LearnExperienceState = this.getState();

        lxState.dockSide = data.dockSide;
        lxState.dockSize = data.dockSize;
    }

    @action.bound
    private _onGetFrameFullOverlay(id:GUID, data:ILearnExperienceFrameFullOverlayData):void
    {
        const lxState:LearnExperienceState = this.getState();

        const responseData:ILearnExperienceFrameFullOverlayData = {
            lxFunction: LearnExperienceFunction.LX_GET_FRAME_FULL_OVERLAY,
            isOverlay: lxState.isOverlay
        };

        this._processLxGetFunc(id, responseData);
    }

    @action.bound
    private _onSetFrameFullOverlay(id:GUID, data:ILearnExperienceFrameFullOverlayData):void
    {
        const lxState:LearnExperienceState = this.getState();

        lxState.isOverlay = data.isOverlay;
    }

    @action.bound
    public toggleLxFrameVisibility():void
    {
        const lxState:LearnExperienceState = this.getState();

        lxState.isVisible = !lxState.isVisible;
    }

    @action.bound
    public activateLxFrame():void
    {
        const lxState:LearnExperienceState = this.getState();

        lxState.isActive = true;
    }

    @action.bound
    public showLxFrame():void
    {
        const lxState:LearnExperienceState = this.getState();

        lxState.isVisible = true;
    }

    @action.bound
    public lxCleanup():void
    {
        const lxState:LearnExperienceState = this.getState();

        lxState.isActive = false;
        lxState.isVisible = false;
    }

    @action.bound
    public setDotButtonZIndex(zIndex:number):void
    {
        const lxState:LearnExperienceState = this.getState();

        lxState.zIndex = zIndex;
    }
}
